import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { casesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
    FolderOpen, 
    LogOut,
    FileText,
    ChevronRight,
    AlertCircle,
    User,
    ArrowUpDown,
    Eye,
    EyeOff
} from 'lucide-react';
import { getStatusColor, getStatusLabel, formatDate } from '../lib/utils';
import ThemeToggle from '../components/ThemeToggle';
import StyleSwitcher from '../components/StyleSwitcher';

export default function AgentDashboard() {
    const { user, logout } = useAuth();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filtri e ordinamento
    const [sortOrder, setSortOrder] = useState('desc');
    const [hideClosed, setHideClosed] = useState(false);

    useEffect(() => {
        loadCases();
    }, []);

    const loadCases = async () => {
        try {
            setLoading(true);
            const response = await casesApi.list();
            setCases(response.data);
        } catch (err) {
            setError('Errore nel caricamento dei casi');
        } finally {
            setLoading(false);
        }
    };

    // Filtra e ordina i casi
    const getFilteredAndSortedCases = () => {
        let filtered = [...cases];
        
        if (hideClosed) {
            filtered = filtered.filter(c => c.status !== 'closed');
        }
        
        filtered.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        return filtered;
    };

    const toggleSortOrder = () => {
        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    return (
        <div className="min-h-screen bg-[#050505]">
            {/* Noise overlay */}
            <div className="noise-overlay" />

            {/* Header */}
            <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <User className="w-6 h-6 text-[#00FF41]" />
                                <span className="font-heading text-lg font-bold text-white">
                                    DASHBOARD AGENTE
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-white/60 text-sm font-mono hidden sm:inline">
                                {user?.full_name}
                            </span>
                            <StyleSwitcher />
                            <ThemeToggle compact />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={logout}
                                data-testid="logout-btn"
                                className="text-white/60 hover:text-white hover:bg-white/10"
                            >
                                <LogOut className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Casi Assegnati</p>
                                <p className="text-2xl font-heading font-bold text-white">{cases.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <FileText className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Casi Aperti</p>
                                <p className="text-2xl font-heading font-bold text-[#00FF41]">
                                    {cases.filter(c => c.status === 'open').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500 p-4 mb-6 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="text-red-500">{error}</span>
                    </div>
                )}

                {/* Cases List */}
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="font-heading text-xl font-bold text-white">
                            I Miei Casi
                        </h2>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={toggleSortOrder}
                                data-testid="sort-cases-btn"
                                className="bg-[#0A0A0A] border-white/20 text-white/80 hover:border-[#00FF41] hover:text-[#00FF41]"
                            >
                                <ArrowUpDown className="w-4 h-4 mr-2" />
                                {sortOrder === 'desc' ? 'Più recenti' : 'Più vecchi'}
                            </Button>
                            
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setHideClosed(!hideClosed)}
                                data-testid="hide-closed-btn"
                                className={`bg-[#0A0A0A] border-white/20 hover:border-[#00FF41] ${
                                    hideClosed 
                                        ? 'text-[#00FF41] border-[#00FF41]' 
                                        : 'text-white/80 hover:text-[#00FF41]'
                                }`}
                            >
                                {hideClosed ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                                {hideClosed ? 'Mostra chiusi' : 'Nascondi chiusi'}
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                        </div>
                    ) : getFilteredAndSortedCases().length === 0 ? (
                        <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
                            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                            <p className="text-white/60">
                                {hideClosed && cases.length > 0 
                                    ? 'Nessun caso aperto. Premi "Mostra chiusi" per vedere tutti i casi.'
                                    : 'Nessun caso assegnato'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {getFilteredAndSortedCases().map((caseItem) => (
                                <Link
                                    key={caseItem.id}
                                    to={`/cases/${caseItem.id}`}
                                    data-testid={`case-card-${caseItem.id}`}
                                    className="block bg-[#0A0A0A] border border-white/10 hover:border-[#00FF41]/50 transition-all p-4 group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41]/50 flex items-center justify-center">
                                                <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono text-[#00FF41] text-sm">
                                                        {caseItem.protocol_number || caseItem.case_number}
                                                    </span>
                                                    <span className={`badge-neon ${getStatusColor(caseItem.status)}`}>
                                                        {getStatusLabel(caseItem.status)}
                                                    </span>
                                                </div>
                                                {/* Agenti vedono solo nome indagato, non cliente */}
                                                {caseItem.investigated?.name && (
                                                    <p className="text-white font-medium mt-1">
                                                        {caseItem.investigated.name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="text-white/40 text-xs uppercase">Creato il</p>
                                                <p className="text-white/80 font-mono text-sm">
                                                    {formatDate(caseItem.created_at)}
                                                </p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[#00FF41] transition-colors" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
